import type { Enemy, EnemyVariant, EliteAbility } from "@/types/enemy.types"
import { enemyTypes } from "@/data/enemy-types"
import type { Position } from "@/types/game.types"
import { generateId } from "@/utils/id-generator"
import type { Hero } from "@/types/hero.types"
import type { Tower } from "@/types/tower.types"
import type { SummonedUnit } from "@/types/summon.types"
import { gameNow } from "@/utils/game-clock"
import {
  ENEMY_AGGRO_RADIUS,
  ENEMY_AGGRO_COMMIT_MS,
  ENEMY_AGGRO_COOLDOWN_MS,
  ENEMY_RETURN_SPEED_MULT,
  ENEMY_MAX_PATH_DISTANCE,
  ENEMY_PROGRESSION_STAT_MULT,
  ENEMY_PROGRESSION_ARMOR_INTERVAL,
} from "@/data/game-settings"

/**
 * Parse enemy type string which may include variant suffix
 * Format: "enemyType" or "enemyType:variant" (e.g., "goblin:elite")
 */
function parseEnemyTypeString(typeString: string): { baseType: string; variant: EnemyVariant } {
  const parts = typeString.split(":")
  const baseType = parts[0]
  const variantStr = parts[1]

  let variant: EnemyVariant = "regular"
  if (variantStr === "elite") {
    variant = "elite"
  } else if (variantStr === "unique") {
    variant = "unique"
  }

  return { baseType, variant }
}

/**
 * Get stat multiplier for enemy variant
 * Regular: 1.0x, Elite: 1.5x, Unique: 2.0x
 */
function getVariantStatMultiplier(variant: EnemyVariant): number {
  switch (variant) {
    case "elite":
      return 1.5
    case "unique":
      return 2.0
    default:
      return 1.0
  }
}

/**
 * Get XP multiplier for enemy variant
 * Regular: 1.0x, Elite: 1.5x, Unique: 3.0x
 */
function getVariantXpMultiplier(variant: EnemyVariant): number {
  switch (variant) {
    case "elite":
      return 1.5
    case "unique":
      return 3.0
    default:
      return 1.0
  }
}

/**
 * Create a new enemy at the given spawn position (world coordinates)
 * Supports variant syntax: "goblin:elite" or "goblin:unique"
 * progressionLevel: bonus level from starmap progression (0-7), scales stats
 * nodesCompleted: number of starmap nodes completed (for aggro disable in first 2 levels)
 */
export function createEnemy(typeString: string, spawnPosition: Position, progressionLevel: number = 0, nodesCompleted: number = 0): Enemy {
  const { baseType, variant } = parseEnemyTypeString(typeString)

  const config = enemyTypes[baseType]
  if (!config) {
    throw new Error(`Enemy type "${baseType}" not found`)
  }

  const variantStatMultiplier = getVariantStatMultiplier(variant)
  const xpMultiplier = getVariantXpMultiplier(variant)

  // Calculate progression stat multiplier (7% compound per level)
  const progressionMultiplier = Math.pow(ENEMY_PROGRESSION_STAT_MULT, progressionLevel)

  // Combine variant and progression multipliers
  const totalStatMultiplier = variantStatMultiplier * progressionMultiplier

  // Apply stat multiplier to health, damage, and reward
  const health = Math.floor(config.stats.health * totalStatMultiplier)
  const damage = {
    min: Math.floor(config.stats.damage.min * totalStatMultiplier),
    max: Math.floor(config.stats.damage.max * totalStatMultiplier),
  }
  const reward = Math.floor(config.stats.reward * variantStatMultiplier) // Reward only scales with variant, not progression
  // XP uses separate multiplier (unique gives 3x instead of 2x)
  const experienceReward = Math.floor(config.stats.experienceReward * xpMultiplier)

  // Armor bonus from progression (+1 every 3 progression levels)
  const armorBonus = Math.floor(progressionLevel / ENEMY_PROGRESSION_ARMOR_INTERVAL)
  const armor = config.stats.armor + armorBonus

  return {
    id: generateId("enemy"),
    type: config.id,
    variant,
    position: { x: spawnPosition.x, y: spawnPosition.y },
    health,
    maxHealth: health,
    speed: config.stats.speed,
    damage,
    towerDamageMultiplier: config.stats.towerDamageMultiplier ?? 0.5,
    attackRange: config.stats.attackRange,
    attackSpeed: config.stats.attackSpeed,
    attackStyle: config.stats.attackStyle,
    damageType: config.stats.damageType,
    defenseType: config.stats.defenseType,
    armor,
    attackCooldown: 0,
    currentWaypointIndex: 0, // Start at waypoint 0, will move toward waypoint 1
    isAttacking: false,
    target: null,
    targetId: undefined,
    reward,
    size: config.stats.size,
    color: config.color,
    experienceReward,
    reachedEnd: false,
    statusEffects: [],
    slowMultiplier: 1.0,
    leavesCorpse: config.stats.leavesCorpse,
    // Animation state
    animationState: "walk",
    animationFrame: 0,
    animationTimer: 0,
    facingDirection: 3, // Start facing down (row 3 in spritesheet)
    previousPosition: { x: spawnPosition.x, y: spawnPosition.y },
    hurtTimer: 0,
    isDying: false,
    deathAnimationFrame: 0,
    lastDamageTime: 0,
    // Aggro system state
    aggroRadius: config.stats.aggroRadius ?? ENEMY_AGGRO_RADIUS,
    aggroTargetPosition: null,
    aggroCommitUntil: 0,
    lastPathPosition: null,
    aggroCooldownUntil: 0,
    isReturningToPath: false,
    // Progression scaling
    progressionLevel,
    nodesCompleted,
    // Elite ability state (only for elite/unique variants with abilities)
    eliteAbility: (variant === "elite" || variant === "unique") && config.eliteAbility ? config.eliteAbility : undefined,
    eliteAbilityCooldown: 0,
    isPhaseShifted: false,
    phaseShiftEndFrame: 0,
    wasResurrected: false,
  }
}

/**
 * Calculate facing direction from movement delta
 * Returns row index in spritesheet: 0=up, 1=left, 2=right, 3=down
 * (matches standard RPG maker spritesheet layout)
 */
function calculateFacingDirection(dx: number, dy: number): number {
  // Determine primary direction based on larger axis
  if (Math.abs(dx) > Math.abs(dy)) {
    // Horizontal movement is dominant
    return dx < 0 ? 1 : 2 // left or right
  } else {
    // Vertical movement is dominant
    return dy < 0 ? 0 : 3 // up (row 0) or down (row 3)
  }
}

/**
 * Find the nearest point on the path to a given position
 * Checks each path segment and returns the closest point
 */
function findNearestPathPoint(position: Position, path: Position[]): { point: Position; distance: number } {
  if (path.length === 0) {
    return { point: position, distance: 0 }
  }

  let nearestPoint = path[0]
  let nearestDistSq = Number.POSITIVE_INFINITY

  // Check each path segment
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]

    // Find nearest point on line segment AB to position P
    const abX = b.x - a.x
    const abY = b.y - a.y
    const apX = position.x - a.x
    const apY = position.y - a.y

    const abLenSq = abX * abX + abY * abY
    if (abLenSq === 0) {
      // Degenerate segment (point)
      const distSq = apX * apX + apY * apY
      if (distSq < nearestDistSq) {
        nearestDistSq = distSq
        nearestPoint = a
      }
      continue
    }

    // Project P onto line AB, clamped to [0, 1]
    const t = Math.max(0, Math.min(1, (apX * abX + apY * abY) / abLenSq))

    // Find the closest point on the segment
    const closestX = a.x + t * abX
    const closestY = a.y + t * abY

    const dx = position.x - closestX
    const dy = position.y - closestY
    const distSq = dx * dx + dy * dy

    if (distSq < nearestDistSq) {
      nearestDistSq = distSq
      nearestPoint = { x: closestX, y: closestY }
    }
  }

  // Also check the last waypoint
  const lastPoint = path[path.length - 1]
  const dx = position.x - lastPoint.x
  const dy = position.y - lastPoint.y
  const distSq = dx * dx + dy * dy
  if (distSq < nearestDistSq) {
    nearestDistSq = distSq
    nearestPoint = lastPoint
  }

  return { point: nearestPoint, distance: Math.sqrt(nearestDistSq) }
}

/**
 * Find a valid aggro target within the enemy's aggro radius
 * Priority: hero > summons > soldiers > towers
 * Disabled for enemies in the first 2 levels (nodesCompleted < 2)
 */
function findAggroTarget(
  enemy: Enemy,
  hero: Hero | null,
  towers: Tower[],
  summons: SummonedUnit[],
  soldiers: { id: string; position: Position; health: number; isDead: boolean }[],
): { position: Position; type: "hero" | "summon" | "soldier" | "tower"; id: string } | null {
  // Disable aggro for enemies in first 2 levels (nodes 0-1)
  // This makes early game easier by letting enemies just follow the path
  if (enemy.nodesCompleted < 2) {
    return null
  }

  const aggroRadiusSq = enemy.aggroRadius * enemy.aggroRadius

  // Check hero first (highest priority)
  if (hero && hero.health > 0) {
    const dx = enemy.position.x - hero.position.x
    const dy = enemy.position.y - hero.position.y
    if (dx * dx + dy * dy <= aggroRadiusSq) {
      return { position: hero.position, type: "hero", id: hero.id }
    }
  }

  // Check summons
  for (const summon of summons) {
    if (summon.health < 1) continue
    const dx = enemy.position.x - summon.position.x
    const dy = enemy.position.y - summon.position.y
    if (dx * dx + dy * dy <= aggroRadiusSq) {
      return { position: summon.position, type: "summon", id: summon.id }
    }
  }

  // Check soldiers
  for (const soldier of soldiers) {
    if (soldier.health <= 0 || soldier.isDead) continue
    const dx = enemy.position.x - soldier.position.x
    const dy = enemy.position.y - soldier.position.y
    if (dx * dx + dy * dy <= aggroRadiusSq) {
      return { position: soldier.position, type: "soldier", id: soldier.id }
    }
  }

  // Check towers
  for (const tower of towers) {
    const dx = enemy.position.x - tower.position.x
    const dy = enemy.position.y - tower.position.y
    if (dx * dx + dy * dy <= aggroRadiusSq) {
      return { position: tower.position, type: "tower", id: tower.id }
    }
  }

  return null
}

/**
 * Update an enemy's position and state for one frame
 * Includes aggro system: enemies will leave path to chase targets within aggro radius
 *
 * @param enemy - The enemy to update
 * @param path - Array of waypoints in world coordinates
 * @param hero - The player's hero (for combat targeting)
 * @param towers - All towers (for combat targeting)
 * @param summons - Summoned units (for combat targeting)
 * @param soldiers - Barracks soldiers (for combat targeting)
 */
export function updateEnemy(enemy: Enemy, path: Position[], hero: Hero | null, towers: Tower[], summons: SummonedUnit[] = [], soldiers: { id: string; position: Position; health: number; isDead: boolean }[] = []): Enemy {
  // Clone the enemy to avoid mutation
  const updated: Enemy = { ...enemy }
  const now = gameNow()

  // Store previous position for direction calculation
  updated.previousPosition = { ...enemy.position }

  // Update hurt timer
  if (updated.hurtTimer > 0) {
    updated.hurtTimer--
  }

  // Update attack cooldown (in frames, not ms)
  if (updated.attackCooldown > 0) {
    updated.attackCooldown = Math.max(0, updated.attackCooldown - 1)
  }

  // If dying, only update death animation
  if (updated.isDying) {
    updated.animationState = "death"
    return updated
  }

  // If already reached the end, don't update further
  if (updated.reachedEnd) {
    return updated
  }

  // Check for targets to attack (within melee/attack range)
  const attackRangeSq = updated.attackRange * updated.attackRange
  let foundAttackTarget = false

  // Check hero first
  if (hero && hero.health > 0) {
    const dx = updated.position.x - hero.position.x
    const dy = updated.position.y - hero.position.y
    if (dx * dx + dy * dy <= attackRangeSq) {
      updated.isAttacking = true
      updated.target = "hero"
      updated.targetId = hero.id
      foundAttackTarget = true
      updated.facingDirection = calculateFacingDirection(-dx, -dy)
    }
  }

  // Check summons if no hero target
  if (!foundAttackTarget) {
    for (const summon of summons) {
      if (summon.health < 1) continue
      const dx = updated.position.x - summon.position.x
      const dy = updated.position.y - summon.position.y
      if (dx * dx + dy * dy <= attackRangeSq) {
        updated.isAttacking = true
        updated.target = "summon"
        updated.targetId = summon.id
        foundAttackTarget = true
        updated.facingDirection = calculateFacingDirection(-dx, -dy)
        break
      }
    }
  }

  // Check soldiers if no summon target
  if (!foundAttackTarget) {
    for (const soldier of soldiers) {
      if (soldier.health <= 0 || soldier.isDead) continue
      const dx = updated.position.x - soldier.position.x
      const dy = updated.position.y - soldier.position.y
      if (dx * dx + dy * dy <= attackRangeSq) {
        updated.isAttacking = true
        updated.target = "soldier"
        updated.targetId = soldier.id
        foundAttackTarget = true
        updated.facingDirection = calculateFacingDirection(-dx, -dy)
        break
      }
    }
  }

  // Check towers if no soldier target
  if (!foundAttackTarget) {
    for (const tower of towers) {
      const dx = updated.position.x - tower.position.x
      const dy = updated.position.y - tower.position.y
      if (dx * dx + dy * dy <= attackRangeSq) {
        updated.isAttacking = true
        updated.target = "tower"
        updated.targetId = tower.id
        foundAttackTarget = true
        updated.facingDirection = calculateFacingDirection(-dx, -dy)
        break
      }
    }
  }

  // If attacking (in melee range), don't move but update animation state
  if (foundAttackTarget) {
    updated.animationState = updated.hurtTimer > 0 ? "hurt" : "attack"
    // Clear aggro state since we're attacking
    updated.aggroTargetPosition = null
    updated.isReturningToPath = false
    return updated
  }

  // Not in melee range, clear combat state
  updated.isAttacking = false
  updated.target = null
  updated.targetId = undefined

  // Check if we've reached the end of the path
  if (updated.currentWaypointIndex >= path.length) {
    updated.reachedEnd = true
    return updated
  }

  // ========== AGGRO SYSTEM LOGIC ==========

  // PHASE 1: If returning to path, move toward lastPathPosition
  if (updated.isReturningToPath && updated.lastPathPosition) {
    const returnSpeed = updated.speed * updated.slowMultiplier * ENEMY_RETURN_SPEED_MULT
    const dx = updated.lastPathPosition.x - updated.position.x
    const dy = updated.lastPathPosition.y - updated.position.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist < returnSpeed + 5) {
      // Reached path position, resume normal pathfinding
      updated.position = { ...updated.lastPathPosition }
      updated.isReturningToPath = false
      updated.lastPathPosition = null
      updated.aggroCooldownUntil = now + ENEMY_AGGRO_COOLDOWN_MS
    } else {
      // Move toward path position
      const angle = Math.atan2(dy, dx)
      updated.position = {
        x: updated.position.x + Math.cos(angle) * returnSpeed,
        y: updated.position.y + Math.sin(angle) * returnSpeed,
      }
      updated.facingDirection = calculateFacingDirection(Math.cos(angle), Math.sin(angle))
      updated.animationState = updated.hurtTimer > 0 ? "hurt" : "walk"
      return updated
    }
  }

  // PHASE 2: Check for aggro targets (if not on cooldown)
  const canAggro = now >= updated.aggroCooldownUntil
  const isCommitted = now < updated.aggroCommitUntil

  if (canAggro || isCommitted) {
    const aggroTarget = findAggroTarget(updated, hero, towers, summons, soldiers)

    if (aggroTarget) {
      // Found a target within aggro radius - chase it!
      const targetPos = aggroTarget.position

      // Save path position if not already chasing
      if (!updated.aggroTargetPosition) {
        updated.lastPathPosition = { ...updated.position }
        updated.aggroCommitUntil = now + ENEMY_AGGRO_COMMIT_MS
      }

      // Update aggro target position (track moving targets)
      updated.aggroTargetPosition = { ...targetPos }

      // Check distance from path - if too far and commitment expired, start returning
      const pathInfo = findNearestPathPoint(updated.position, path)
      if (pathInfo.distance > ENEMY_MAX_PATH_DISTANCE && !isCommitted) {
        // Too far from path and commitment expired - return to path
        updated.isReturningToPath = true
        updated.aggroTargetPosition = null
        updated.aggroCommitUntil = 0
        return updated
      }

      // Move toward aggro target
      const dx = targetPos.x - updated.position.x
      const dy = targetPos.y - updated.position.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist > 1) {
        const effectiveSpeed = updated.speed * updated.slowMultiplier
        const angle = Math.atan2(dy, dx)
        updated.position = {
          x: updated.position.x + Math.cos(angle) * effectiveSpeed,
          y: updated.position.y + Math.sin(angle) * effectiveSpeed,
        }
        updated.facingDirection = calculateFacingDirection(dx, dy)
      }

      updated.animationState = updated.hurtTimer > 0 ? "hurt" : "walk"
      return updated
    } else if (updated.aggroTargetPosition) {
      // Had a target but lost it
      if (isCommitted) {
        // Still committed - move to last known position
        const dx = updated.aggroTargetPosition.x - updated.position.x
        const dy = updated.aggroTargetPosition.y - updated.position.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist > 5) {
          const effectiveSpeed = updated.speed * updated.slowMultiplier
          const angle = Math.atan2(dy, dx)
          updated.position = {
            x: updated.position.x + Math.cos(angle) * effectiveSpeed,
            y: updated.position.y + Math.sin(angle) * effectiveSpeed,
          }
          updated.facingDirection = calculateFacingDirection(dx, dy)
          updated.animationState = updated.hurtTimer > 0 ? "hurt" : "walk"
          return updated
        }
      }
      // Commitment expired or reached last position - return to path
      updated.isReturningToPath = true
      updated.aggroTargetPosition = null
      updated.aggroCommitUntil = 0
      return updated
    }
  }

  // Clear any stale aggro state if we get here
  if (updated.aggroTargetPosition && !isCommitted) {
    updated.aggroTargetPosition = null
    updated.isReturningToPath = true
    return updated
  }

  // ========== NORMAL PATH FOLLOWING ==========

  const waypoint = path[updated.currentWaypointIndex]

  // Calculate distance and angle to waypoint
  const xDistance = waypoint.x - updated.position.x
  const yDistance = waypoint.y - updated.position.y
  const angle = Math.atan2(yDistance, xDistance)

  // Calculate velocity (speed is pixels per frame), apply slow multiplier and blood rage bonus
  const bloodRageSpeedMult = getBloodRageMovementSpeedMultiplier(updated)
  const effectiveSpeed = updated.speed * updated.slowMultiplier * bloodRageSpeedMult
  const velocityX = Math.cos(angle) * effectiveSpeed
  const velocityY = Math.sin(angle) * effectiveSpeed

  // Move enemy
  updated.position = {
    x: updated.position.x + velocityX,
    y: updated.position.y + velocityY,
  }

  // Update facing direction based on movement
  if (Math.abs(velocityX) > 0.1 || Math.abs(velocityY) > 0.1) {
    updated.facingDirection = calculateFacingDirection(velocityX, velocityY)
  }

  // Update animation state
  if (updated.hurtTimer > 0) {
    updated.animationState = "hurt"
  } else {
    updated.animationState = "walk"
  }

  // Check if we've reached the waypoint
  if (
    Math.abs(Math.round(updated.position.x) - Math.round(waypoint.x)) < Math.abs(velocityX) + 1 &&
    Math.abs(Math.round(updated.position.y) - Math.round(waypoint.y)) < Math.abs(velocityY) + 1
  ) {
    // Snap to waypoint to avoid drift
    updated.position = { x: waypoint.x, y: waypoint.y }
    updated.currentWaypointIndex++

    // Check if that was the last waypoint
    if (updated.currentWaypointIndex >= path.length) {
      updated.reachedEnd = true
    }
  }

  return updated
}

/**
 * Check if enemy should perform an attack this frame
 */
export function shouldEnemyAttack(enemy: Enemy): boolean {
  return enemy.isAttacking && enemy.attackCooldown <= 0
}

/**
 * Perform enemy attack (resets cooldown in frames)
 */
export function performEnemyAttack(enemy: Enemy): Enemy {
  // Apply blood rage attack speed bonus (lower cooldown = faster attacks)
  const bloodRageAttackMult = getBloodRageAttackSpeedMultiplier(enemy)
  // Blood rage increases attack speed, so reduce cooldown (divide by multiplier)
  const effectiveCooldown = Math.max(1, Math.floor(enemy.attackSpeed / bloodRageAttackMult))
  return {
    ...enemy,
    attackCooldown: effectiveCooldown,
  }
}

/**
 * Apply damage to an enemy
 * @param enemy - The enemy to damage
 * @param damage - Amount of damage to apply
 * @param sourcePosition - Optional position of the damage source (for death direction)
 */
export function damageEnemy(enemy: Enemy, damage: number, sourcePosition?: Position): Enemy {
  const newHealth = Math.max(0, enemy.health - damage)
  return {
    ...enemy,
    health: newHealth,
    hurtTimer: 10, // Show hurt animation for 10 frames
    isDying: newHealth <= 0,
    lastDamageSourcePosition: sourcePosition || enemy.lastDamageSourcePosition,
    lastDamageTime: gameNow(),
  }
}

/**
 * Check if enemy is dead
 */
export function isEnemyDead(enemy: Enemy): boolean {
  return enemy.health <= 0
}

/**
 * Calculate the facing direction for a corpse (opposite to the killing blow direction)
 * Returns row index in spritesheet: 0=up, 1=left, 2=right, 3=down
 */
export function getCorpseFacingDirection(enemy: Enemy): number {
  if (!enemy.lastDamageSourcePosition) {
    // Default to enemy's current facing direction
    return enemy.facingDirection
  }

  // Calculate direction FROM damage source TO enemy (opposite of killing blow)
  const dx = enemy.position.x - enemy.lastDamageSourcePosition.x
  const dy = enemy.position.y - enemy.lastDamageSourcePosition.y

  // Determine primary direction based on larger axis
  if (Math.abs(dx) > Math.abs(dy)) {
    // Horizontal - corpse faces away from source
    return dx < 0 ? 2 : 1 // facing right (row 2) or facing left (row 1)
  } else {
    // Vertical - corpse faces away from source
    return dy < 0 ? 0 : 3 // facing up (row 0) or facing down (row 3)
  }
}

/**
 * Calculate enemy attack damage from their damage range
 */
export function calculateEnemyDamage(enemy: Enemy): number {
  const { min, max } = enemy.damage
  return Math.floor(min + Math.random() * (max - min + 1))
}

/**
 * Check if enemy has reached the base (end of path)
 */
export function hasEnemyReachedBase(enemy: Enemy): boolean {
  return enemy.reachedEnd
}

/**
 * Update status effects on an enemy (tick down durations, recalculate multipliers)
 */
export function updateEnemyStatusEffects(enemy: Enemy): Enemy {
  if (!enemy.statusEffects || enemy.statusEffects.length === 0) {
    return enemy
  }

  // Tick down durations and filter out expired effects
  const activeEffects = enemy.statusEffects
    .map((effect) => ({ ...effect, duration: effect.duration - 1 }))
    .filter((effect) => effect.duration > 0)

  // Check for root effect first (root completely stops movement)
  const isRooted = activeEffects.some((effect) => effect.type === "root")

  // If rooted, slow multiplier is 0 (no movement)
  let slowMultiplier = isRooted ? 0 : 1.0

  // If not rooted, apply slow effects multiplicatively
  if (!isRooted) {
    for (const effect of activeEffects) {
      if (effect.type === "slow") {
        slowMultiplier *= effect.value
      }
    }
  }

  return {
    ...enemy,
    statusEffects: activeEffects,
    slowMultiplier,
  }
}

/**
 * Check if an enemy is rooted
 */
export function isEnemyRooted(enemy: Enemy): boolean {
  return enemy.statusEffects.some((effect) => effect.type === "root")
}

/**
 * Check if an enemy is slowed
 */
export function isEnemySlowed(enemy: Enemy): boolean {
  return enemy.slowMultiplier < 1.0
}

// ===== ELITE ABILITY HELPER FUNCTIONS =====

/**
 * Check if an enemy has an active elite ability
 */
export function hasEliteAbility(enemy: Enemy): boolean {
  return enemy.eliteAbility !== undefined && (enemy.variant === "elite" || enemy.variant === "unique")
}

/**
 * Get the elite ability for an enemy (if any)
 */
export function getEliteAbility(enemy: Enemy): EliteAbility | undefined {
  if (!hasEliteAbility(enemy)) return undefined
  return enemy.eliteAbility
}

/**
 * Check if enemy has a specific elite ability effect
 */
export function hasEliteAbilityEffect(enemy: Enemy, effect: EliteAbility["effect"]): boolean {
  return hasEliteAbility(enemy) && enemy.eliteAbility?.effect === effect
}

/**
 * Get damage reduction multiplier for a specific attack type
 * Returns the multiplier to apply to damage (e.g., 0.7 for 30% reduction)
 */
export function getEliteDamageReduction(enemy: Enemy, attackType: "archer" | "mage" | "siege" | "hero"): number {
  if (!hasEliteAbility(enemy) || !enemy.eliteAbility) return 1.0

  const ability = enemy.eliteAbility

  // Check for matching damage reduction ability
  if (
    (attackType === "archer" && ability.effect === "damage-reduction-archer") ||
    (attackType === "mage" && ability.effect === "damage-reduction-mage") ||
    (attackType === "siege" && ability.effect === "damage-reduction-siege") ||
    (attackType === "hero" && ability.effect === "damage-reduction-hero")
  ) {
    return 1.0 - ability.value // e.g., 0.30 value = 0.70 multiplier (30% reduction)
  }

  return 1.0
}

/**
 * Check if enemy is currently phase shifted (invulnerable)
 */
export function isEnemyPhaseShifted(enemy: Enemy): boolean {
  return enemy.isPhaseShifted
}

/**
 * Trigger phase shift ability (makes enemy invulnerable)
 */
export function triggerPhaseShift(enemy: Enemy, currentFrame: number): Enemy {
  if (!hasEliteAbilityEffect(enemy, "phase-shift") || !enemy.eliteAbility) return enemy
  if (enemy.eliteAbilityCooldown > 0) return enemy // On cooldown

  const duration = enemy.eliteAbility.duration ?? 120 // 2 seconds default
  const cooldown = enemy.eliteAbility.cooldown ?? 600 // 10 seconds default

  return {
    ...enemy,
    isPhaseShifted: true,
    phaseShiftEndFrame: currentFrame + duration,
    eliteAbilityCooldown: cooldown,
  }
}

/**
 * Update phase shift state (check if it should end)
 */
export function updatePhaseShift(enemy: Enemy, currentFrame: number): Enemy {
  if (!enemy.isPhaseShifted) return enemy

  if (currentFrame >= enemy.phaseShiftEndFrame) {
    return {
      ...enemy,
      isPhaseShifted: false,
    }
  }

  return enemy
}

/**
 * Update elite ability cooldown
 */
export function updateEliteAbilityCooldown(enemy: Enemy): Enemy {
  if (enemy.eliteAbilityCooldown <= 0) return enemy

  return {
    ...enemy,
    eliteAbilityCooldown: Math.max(0, enemy.eliteAbilityCooldown - 1),
  }
}

/**
 * Check if enemy's periodic ability is ready
 */
export function isPeriodicAbilityReady(enemy: Enemy): boolean {
  if (!hasEliteAbility(enemy) || !enemy.eliteAbility) return false
  if (enemy.eliteAbility.trigger !== "periodic") return false
  return enemy.eliteAbilityCooldown <= 0
}

/**
 * Reset periodic ability cooldown after use
 */
export function resetPeriodicAbilityCooldown(enemy: Enemy): Enemy {
  if (!enemy.eliteAbility?.cooldown) return enemy

  return {
    ...enemy,
    eliteAbilityCooldown: enemy.eliteAbility.cooldown,
  }
}

/**
 * Check if enemy should apply blood rage (low health bonus)
 */
export function hasBloodRage(enemy: Enemy): boolean {
  if (!hasEliteAbilityEffect(enemy, "blood-rage")) return false
  return enemy.health <= enemy.maxHealth * 0.5 // Below 50% health
}

/**
 * Get blood rage attack speed multiplier
 */
export function getBloodRageAttackSpeedMultiplier(enemy: Enemy): number {
  if (!hasBloodRage(enemy) || !enemy.eliteAbility) return 1.0
  return 1.0 + enemy.eliteAbility.value // e.g., 0.20 value = 1.20 multiplier (20% faster)
}

/**
 * Get blood rage movement speed multiplier
 */
export function getBloodRageMovementSpeedMultiplier(enemy: Enemy): number {
  if (!hasBloodRage(enemy) || !enemy.eliteAbility) return 1.0
  return 1.0 + enemy.eliteAbility.value * 0.5 // Half the bonus for movement (10% instead of 20%)
}

/**
 * Check if enemy has lifesteal ability
 */
export function hasLifesteal(enemy: Enemy): boolean {
  return hasEliteAbilityEffect(enemy, "lifesteal")
}

/**
 * Get lifesteal percentage
 */
export function getLifestealPercent(enemy: Enemy): number {
  if (!hasLifesteal(enemy) || !enemy.eliteAbility) return 0
  return enemy.eliteAbility.value // e.g., 0.15 = 15% lifesteal
}

/**
 * Apply lifesteal healing to enemy
 */
export function applyLifesteal(enemy: Enemy, damageDealt: number): Enemy {
  const lifestealPercent = getLifestealPercent(enemy)
  if (lifestealPercent <= 0) return enemy

  const healAmount = Math.floor(damageDealt * lifestealPercent)
  const newHealth = Math.min(enemy.maxHealth, enemy.health + healAmount)

  return {
    ...enemy,
    health: newHealth,
  }
}

/**
 * Check if enemy has the infernal aura (reduces nearby tower damage)
 */
export function hasInfernalAura(enemy: Enemy): boolean {
  return hasEliteAbilityEffect(enemy, "aura-damage-reduction")
}

/**
 * Get infernal aura radius
 */
export function getInfernalAuraRadius(enemy: Enemy): number {
  if (!hasInfernalAura(enemy) || !enemy.eliteAbility) return 0
  return enemy.eliteAbility.radius ?? 150
}

/**
 * Get infernal aura damage reduction value
 */
export function getInfernalAuraDamageReduction(enemy: Enemy): number {
  if (!hasInfernalAura(enemy) || !enemy.eliteAbility) return 0
  return enemy.eliteAbility.value // e.g., 0.15 = 15% less damage
}

/**
 * Check if enemy is immune to slow effects (demon elite ability)
 */
export function isImmuneToSlow(enemy: Enemy): boolean {
  return hasEliteAbilityEffect(enemy, "aura-damage-reduction") // Demons with infernal aura are immune to slow
}

/**
 * Calculate damage reduction from nearby demons' infernal aura
 * @param enemies - All enemies on the field
 * @param towerPosition - Position of the tower firing
 * @returns Damage multiplier (e.g., 0.85 = 15% reduction)
 */
export function getInfernalAuraDamageMultiplier(enemies: Enemy[], towerPosition: { x: number; y: number }): number {
  // Find all demons with infernal aura and check if tower is in range
  for (const enemy of enemies) {
    if (!hasInfernalAura(enemy) || enemy.health <= 0) continue

    const auraRadius = getInfernalAuraRadius(enemy)
    const dx = towerPosition.x - enemy.position.x
    const dy = towerPosition.y - enemy.position.y
    const distSq = dx * dx + dy * dy

    if (distSq <= auraRadius * auraRadius) {
      // Tower is in demon's aura - apply damage reduction
      // Only apply strongest aura (don't stack)
      return 1 - getInfernalAuraDamageReduction(enemy)
    }
  }

  return 1 // No aura affecting this tower
}

/**
 * Check if elite golem has passive regeneration
 * The golem's Fortified ability includes 5 HP/sec regen
 */
export function hasPassiveRegeneration(enemy: Enemy): boolean {
  // Golem has regen as part of their Fortified ability
  return enemy.type === "golem" && (enemy.variant === "elite" || enemy.variant === "unique")
}

/**
 * Get passive regeneration amount per frame
 * Golem regenerates 5 HP/sec = 5/60 per frame
 */
export function getPassiveRegenPerFrame(enemy: Enemy): number {
  if (!hasPassiveRegeneration(enemy)) return 0
  return 5 / 60 // 5 HP per second at 60fps
}

/**
 * Apply passive regeneration to an enemy
 */
export function applyPassiveRegeneration(enemy: Enemy): Enemy {
  const regenPerFrame = getPassiveRegenPerFrame(enemy)
  if (regenPerFrame <= 0 || enemy.health >= enemy.maxHealth || enemy.health <= 0) return enemy

  return {
    ...enemy,
    health: Math.min(enemy.maxHealth, enemy.health + regenPerFrame),
  }
}

/**
 * Check if enemy has gold steal ability
 */
export function hasGoldSteal(enemy: Enemy): boolean {
  return hasEliteAbilityEffect(enemy, "gold-steal")
}

/**
 * Get gold steal amount
 */
export function getGoldStealAmount(enemy: Enemy): number {
  if (!hasGoldSteal(enemy) || !enemy.eliteAbility) return 0
  return enemy.eliteAbility.value // e.g., 5 gold
}

/**
 * Check if enemy has lycanthropy ability (converts killed soldiers to werewolves)
 */
export function hasLycanthropy(enemy: Enemy): boolean {
  return hasEliteAbilityEffect(enemy, "lycanthropy")
}

/**
 * Check if enemy has slow-attacker ability (slows soldiers/heroes that attack it)
 */
export function hasSlowAttacker(enemy: Enemy): boolean {
  return hasEliteAbilityEffect(enemy, "slow-attacker")
}

/**
 * Get the slow-attacker effect parameters
 * Returns { slowPercent, duration } for applying to attacking units
 */
export function getSlowAttackerEffect(enemy: Enemy): { slowPercent: number; duration: number } | null {
  if (!hasSlowAttacker(enemy) || !enemy.eliteAbility) return null
  return {
    slowPercent: enemy.eliteAbility.value, // e.g., 0.15 = 15% attack speed reduction
    duration: enemy.eliteAbility.duration ?? 180, // Default 3 seconds
  }
}

/**
 * Create a werewolf enemy from a soldier killed by a werewolf (lycanthropy)
 * Spawns a regular werewolf at the soldier's death position
 */
export function createWerewolfFromSoldier(position: Position, progressionLevel: number, nodesCompleted: number): Enemy {
  const werewolf = createEnemy("werewolf", position, progressionLevel, nodesCompleted)
  // Mark as transformed so it can be differentiated if needed
  return {
    ...werewolf,
    wasResurrected: true, // Reuse this flag to mark as transformed (can't be resurrected by lich)
  }
}

/**
 * Check if enemy can be resurrected by a lich
 */
export function canBeResurrected(enemy: Enemy): boolean {
  // Only zombies and skeletons can be resurrected
  if (enemy.type !== "zombie" && enemy.type !== "skeleton") return false
  // Can't resurrect enemies that were already resurrected
  if (enemy.wasResurrected) return false
  // Must be dead
  if (enemy.health > 0) return false
  return true
}

/**
 * Create a resurrected version of an enemy
 */
export function createResurrectedEnemy(deadEnemy: Enemy): Enemy {
  // Create at 50% health
  const health = Math.floor(deadEnemy.maxHealth * 0.5)

  return {
    ...deadEnemy,
    id: generateId("enemy"), // New ID
    health,
    maxHealth: health,
    isDying: false,
    deathAnimationFrame: 0,
    animationState: "walk",
    animationFrame: 0,
    hurtTimer: 0,
    wasResurrected: true, // Mark as resurrected so it can't be resurrected again
    statusEffects: [],
    slowMultiplier: 1.0,
    attackCooldown: 0,
    isAttacking: false,
    target: null,
    targetId: undefined,
    reachedEnd: false,
  }
}

/**
 * Apply elite damage reduction to incoming damage
 * @param enemy - The enemy being damaged
 * @param damage - The base damage amount
 * @param sourceType - The type of damage source (archer, mage, siege, hero)
 * @returns The reduced damage amount
 */
export function applyEliteDamageReduction(enemy: Enemy, damage: number, sourceType: "archer" | "mage" | "siege" | "hero"): number {
  const reduction = getEliteDamageReduction(enemy, sourceType)
  return Math.floor(damage * reduction)
}

/**
 * Damage enemy with elite ability checks (phase shift, damage reduction)
 * Returns null if damage was completely negated (phase shifted)
 */
export function damageEnemyWithAbilities(
  enemy: Enemy,
  damage: number,
  sourcePosition: Position | undefined,
  sourceType: "archer" | "mage" | "siege" | "hero" | "other",
  currentFrame: number
): { enemy: Enemy; damageDealt: number; wasPhaseShifted: boolean } {
  // Check if enemy is phase shifted (invulnerable)
  if (isEnemyPhaseShifted(enemy)) {
    return { enemy, damageDealt: 0, wasPhaseShifted: true }
  }

  // Apply elite damage reduction
  let finalDamage = damage
  if (sourceType !== "other") {
    finalDamage = applyEliteDamageReduction(enemy, damage, sourceType)
  }

  // Apply damage
  let updatedEnemy = damageEnemy(enemy, finalDamage, sourcePosition)

  // Check for phase shift trigger (on-damaged ability)
  if (hasEliteAbilityEffect(enemy, "phase-shift") && finalDamage > 0) {
    updatedEnemy = triggerPhaseShift(updatedEnemy, currentFrame)
  }

  return { enemy: updatedEnemy, damageDealt: finalDamage, wasPhaseShifted: false }
}
